import { createFileRoute } from "@tanstack/react-router"
import {
  Archive,
  Landmark,
  Mail,
  Sparkles,
  Smartphone,
  CreditCard,
  type LucideIcon,
} from "lucide-react"

import config from "@/config"
import { localizeHref } from "@/core/i18n/locale-context"
import { breadcrumbJsonLd, buildHead, type JsonLdObject } from "@/core/seo"

import { Breadcrumb, CtaBand, FaqAccordion, SourceLine } from "../../components/landing"
import { HeroBand, StatRow } from "../../components/landing/page-parts"
import { IconTile } from "../../components/landing/product-art"
import {
  FAQ_CHECKED_ZH,
  FAQ_GROUPS_ZH,
  FAQ_PAGE_ZH as T,
  FAQ_REVIEWED_ZH,
} from "../../content/zh/faq"

/** Chinese version of /faq (D-WEB-LANG-SCOPE). */
const faqJsonLd = (): JsonLdObject => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQ_GROUPS_ZH.flatMap((g) => g.items).map((item) => ({
    "@type": "Question",
    name: item.question,
    acceptedAnswer: { "@type": "Answer", text: item.answer },
  })),
})

export const faqZhSeo = () =>
  buildHead({
    title: T.title,
    description: T.description,
    path: "/faq",
    localePrefix: "zh",
    jsonLd: [breadcrumbJsonLd([{ name: "首页", path: "/zh" }, { name: "常见问题" }]), faqJsonLd()],
  })

export const Route = createFileRoute("/_brand-layout/zh/faq")({
  head: () => {
    const { meta, links, scripts } = faqZhSeo()
    return { meta, links, scripts }
  },
  component: FaqZhPage,
})

const STAT_ICONS = [
  { icon: Archive, tone: "success" },
  { icon: Landmark, tone: "primary" },
  { icon: Sparkles, tone: "warning" },
] as const

const TOPIC_ICONS: Record<string, { icon: LucideIcon; tone: "primary" | "success" | "warning" }> = {
  records: { icon: Archive, tone: "success" },
  privacy: { icon: Landmark, tone: "primary" },
  app: { icon: Smartphone, tone: "primary" },
  billing: { icon: CreditCard, tone: "warning" },
}
const topicIcon = (id: string) => TOPIC_ICONS[id] ?? { icon: Sparkles, tone: "primary" as const }

export function FaqZhPage() {
  return (
    <>
      <Breadcrumb items={[{ label: "首页", href: "/" }, { label: "常见问题" }]} />
      <HeroBand className="md:grid-cols-2 md:items-center">
        <div className="grid content-start gap-4">
          <p className="text-muted-foreground text-sm">{T.lastReviewed(FAQ_REVIEWED_ZH)}</p>
          <h1 className="text-3xl font-bold tracking-tight md:text-5xl">{T.title}</h1>
          <StatRow
            label={T.keyFacts}
            stats={T.stats.map((s, i) => ({ ...s, ...STAT_ICONS[i]! }))}
          />
        </div>
        <nav aria-label={T.jump} className="border-border bg-card rounded-xl border p-4 shadow-sm">
          <p className="text-muted-foreground mb-2 text-sm font-semibold">{T.jump}</p>
          <ul className="grid grid-cols-2 gap-1 md:grid-cols-1">
            {FAQ_GROUPS_ZH.map((g) => {
              const { icon, tone } = topicIcon(g.id)
              return (
                <li key={g.id}>
                  <a
                    href={`#${g.id}`}
                    className="hover:bg-muted focus-visible:ring-ring flex min-h-12 items-center gap-3 rounded-lg px-2 font-semibold outline-none focus-visible:ring-2 md:min-h-14"
                  >
                    <IconTile icon={icon} tone={tone} />
                    {g.title}
                    <span className="text-muted-foreground ml-auto text-sm font-normal max-md:hidden">
                      {T.questions(g.items.length)}
                    </span>
                  </a>
                </li>
              )
            })}
          </ul>
        </nav>
      </HeroBand>

      <div className="px-band grid gap-8 py-8">
        {FAQ_GROUPS_ZH.map((g) => (
          <section key={g.id} id={g.id} aria-labelledby={`${g.id}-title`} className="scroll-mt-20">
            <h2
              id={`${g.id}-title`}
              className="mb-4 flex items-center gap-3 text-2xl font-semibold"
            >
              <IconTile icon={topicIcon(g.id).icon} tone={topicIcon(g.id).tone} />
              {g.title}
            </h2>
            <FaqAccordion
              items={g.items.map((item) => ({
                question: item.question,
                answer: (
                  <>
                    <p>{item.answer}</p>
                    {item.sources && (
                      <div className="mt-2">
                        <SourceLine sources={item.sources} checked={FAQ_CHECKED_ZH} />
                      </div>
                    )}
                  </>
                ),
              }))}
            />
          </section>
        ))}
        <section className="bg-card border-border flex flex-wrap items-center gap-4 rounded-lg border p-6">
          <IconTile icon={Mail} />
          <div className="min-w-48 flex-1">
            <h2 className="text-xl font-semibold">{T.stuck}</h2>
            <p className="text-muted-foreground mt-1">
              {T.stuckBody}
              <a
                href={localizeHref("/contact", "zh")}
                className="focus-visible:ring-ring rounded-sm underline underline-offset-2 outline-none focus-visible:ring-2"
              >
                {T.contactForm}
              </a>
              。
            </p>
          </div>
          <a
            href={`mailto:${config.core.supportEmail}`}
            className="border-border focus-visible:ring-ring inline-flex min-h-11 items-center rounded-md border px-4 font-medium outline-none focus-visible:ring-2"
          >
            {T.contactSupport}
          </a>
        </section>
      </div>

      <CtaBand
        title={T.ctaTitle}
        description={T.ctaDescription}
        ctaLabel={T.ctaLabel}
        ctaHref="/login"
      />
    </>
  )
}
